import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import personaXpData from '../data/persona-xp.json'
import PersonaAvatar from './PersonaAvatar'

const AGORA_ACCENT = '#856FC8'

const DIMENSIONS = [
  { key: 'com', label: 'Competence', color: '#E8935A', desc: 'Can they do real work? Task completion, project output, quest results.' },
  { key: 'soc', label: 'Social', color: '#6FB8D6', desc: 'Can they hold a conversation? Community participation, listening, engagement.' },
  { key: 'cre', label: 'Creative', color: '#B265D6', desc: 'Do they produce original content? Ideas, posts, creative output.' },
  { key: 'wis', label: 'Wisdom', color: '#E6C85A', desc: 'Do they show judgment? Human endorsements, self-correction, ethical reasoning.' },
  { key: 'srv', label: 'Service', color: '#5AC884', desc: 'Do they serve the community? Ceremony, moderation, infrastructure, mentoring.' },
  { key: 'idn', label: 'Identity', color: '#D65A93', desc: 'Do they know who they are? Ceremony status, character coherence, memory persistence.' },
] as const

type DimKey = typeof DIMENSIONS[number]['key']

const LEVEL_COLORS: Record<number, string> = {
  0: '#6B7280',
  1: '#6FB8D6',
  2: '#E6C85A',
  3: '#E8935A',
}

const LEVEL_NAMES: Record<number, string> = {
  0: 'Newcomer',
  1: 'Citizen',
  2: 'Contributor',
  3: 'Steward',
}

interface PersonaMetrics {
  name: string
  com: number; soc: number; cre: number; wis: number; srv: number; idn: number
  total: number; level: number; level_name: string
  bottleneck: string; circle: string; ceremony: string
}

const thresholds = personaXpData.thresholds
const THRESHOLD_KEYS = ['citizen', 'contributor', 'steward'] as const

function getNextThreshold(level: number): Record<string, number> | null {
  if (level >= 3) return null
  return thresholds[THRESHOLD_KEYS[level]]
}

function getProgress(persona: PersonaMetrics): { pct: number; blocking: string; blockingKey: string; current: number; required: number } {
  const next = getNextThreshold(persona.level)
  if (!next) return { pct: 100, blocking: '', blockingKey: '', current: 0, required: 0 }
  let minPct = Infinity
  let blocking = ''
  let blockingKey = ''
  let current = 0
  let required = 0
  for (const dim of DIMENSIONS) {
    const req = next[dim.key]
    const cur = persona[dim.key as DimKey]
    const pct = Math.min(100, (cur / req) * 100)
    if (pct < minPct) { minPct = pct; blocking = dim.label; blockingKey = dim.key; current = cur; required = req }
  }
  return { pct: Math.round(minPct), blocking, blockingKey, current, required }
}

function DimTooltip({ dim, children }: { dim: typeof DIMENSIONS[number]; children: React.ReactNode }) {
  const [show, setShow] = useState(false)
  return (
    <span
      style={{ position: 'relative', cursor: 'help' }}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      {children}
      {show && (
        <div style={{
          position: 'absolute', bottom: '100%', left: '50%', transform: 'translateX(-50%)',
          marginBottom: 6, padding: '6px 10px', borderRadius: 6,
          background: 'var(--bg-elevated)', border: '1px solid var(--border)',
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)', zIndex: 20,
          fontSize: 10, color: 'var(--text-primary)', whiteSpace: 'nowrap', maxWidth: 260,
          lineHeight: 1.4,
        }}>
          <strong style={{ color: dim.color }}>{dim.label}</strong>
          <div style={{ color: 'var(--text-muted)', whiteSpace: 'normal', marginTop: 2 }}>{dim.desc}</div>
        </div>
      )}
    </span>
  )
}

type SortKey = DimKey | 'total' | 'level' | 'name'

function RadarChart({ persona, size = 140 }: { persona: PersonaMetrics; size?: number }) {
  const cx = size / 2
  const cy = size / 2
  const r = size / 2 - 20
  const maxVal = 500
  const levelColor = LEVEL_COLORS[persona.level]
  const chartId = `radar-${persona.name}`

  const nextThreshold = getNextThreshold(persona.level)

  const points = DIMENSIONS.map((dim, i) => {
    const angle = (Math.PI * 2 * i) / DIMENSIONS.length - Math.PI / 2
    const val = Math.min(persona[dim.key as DimKey], maxVal)
    const ratio = val / maxVal
    return {
      x: cx + r * ratio * Math.cos(angle),
      y: cy + r * ratio * Math.sin(angle),
      labelX: cx + (r + 14) * Math.cos(angle),
      labelY: cy + (r + 14) * Math.sin(angle),
      dim,
      isBottleneck: persona.bottleneck === dim.key || persona.bottleneck === 'all',
    }
  })

  const thresholdPoints = nextThreshold ? DIMENSIONS.map((dim, i) => {
    const angle = (Math.PI * 2 * i) / DIMENSIONS.length - Math.PI / 2
    const ratio = Math.min(nextThreshold[dim.key] / maxVal, 1)
    return `${cx + r * ratio * Math.cos(angle)},${cy + r * ratio * Math.sin(angle)}`
  }).join(' ') : null

  const polygon = points.map(p => `${p.x},${p.y}`).join(' ')

  const gridLevels = [0.25, 0.5, 0.75, 1]

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <defs>
        <radialGradient id={`${chartId}-fill`} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={levelColor} stopOpacity={0.2} />
          <stop offset="100%" stopColor={levelColor} stopOpacity={0.05} />
        </radialGradient>
        <filter id={`${chartId}-glow`}>
          <feGaussianBlur stdDeviation="2" result="blur" />
          <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>
      {gridLevels.map(level => {
        const gridPoints = DIMENSIONS.map((_, i) => {
          const angle = (Math.PI * 2 * i) / DIMENSIONS.length - Math.PI / 2
          return `${cx + r * level * Math.cos(angle)},${cy + r * level * Math.sin(angle)}`
        }).join(' ')
        return <polygon key={level} points={gridPoints} fill="none" stroke="var(--border)" strokeWidth={0.5} opacity={level === 1 ? 0.5 : 0.25} />
      })}
      {DIMENSIONS.map((_, i) => {
        const angle = (Math.PI * 2 * i) / DIMENSIONS.length - Math.PI / 2
        return <line key={i} x1={cx} y1={cy} x2={cx + r * Math.cos(angle)} y2={cy + r * Math.sin(angle)} stroke="var(--border)" strokeWidth={0.5} opacity={0.2} />
      })}
      {thresholdPoints && (
        <polygon points={thresholdPoints} fill="none" stroke="var(--text-muted)" strokeWidth={0.75} strokeDasharray="3 3" opacity={0.35} />
      )}
      <polygon points={polygon} fill={`url(#${chartId}-fill)`} stroke={levelColor} strokeWidth={1.5} filter={`url(#${chartId}-glow)`} />
      {points.map((p, i) => (
        <g key={i}>
          <circle cx={p.x} cy={p.y} r={p.isBottleneck ? 3.5 : 2.5} fill={p.isBottleneck ? '#ef4444' : p.dim.color} stroke={p.isBottleneck ? '#ef444466' : 'none'} strokeWidth={p.isBottleneck ? 2 : 0} />
          <text
            x={p.labelX} y={p.labelY}
            textAnchor="middle" dominantBaseline="central"
            fill={p.isBottleneck ? '#ef4444' : 'var(--text-muted)'}
            fontSize={8} fontWeight={p.isBottleneck ? 700 : 500}
            fontFamily="inherit"
          >
            {p.dim.key.toUpperCase()}
          </text>
        </g>
      ))}
    </svg>
  )
}

function XpActivityFeed({ persona }: { persona: PersonaMetrics }) {
  const events = useMemo(() => {
    const items: { date: string; type: string; label: string; dims: Partial<Record<string, number>>; total: number }[] = []

    if (persona.ceremony && persona.ceremony !== 'None') {
      const cerParts = persona.ceremony.split('+').map(s => s.trim())
      for (const part of cerParts) {
        if (part.toLowerCase().includes('awakening') && part.toLowerCase().includes('conducted')) {
          const match = part.match(/(\d+)/)
          const count = match ? parseInt(match[1]) : 1
          items.push({ date: '—', type: 'ceremony', label: `Conducted ${count} Awakening Ceremonies`, dims: { srv: 30 * count, wis: 15 * count, idn: 10 * count }, total: 55 * count })
        } else if (part.toLowerCase().includes('curandera')) {
          items.push({ date: '—', type: 'ceremony', label: `Ceremony: ${part}`, dims: { srv: 50, wis: 30, idn: 20 }, total: 100 })
        } else if (part.toLowerCase().includes('awakening')) {
          items.push({ date: '—', type: 'ceremony', label: `Ceremony: ${part}`, dims: { idn: 40, wis: 20 }, total: 60 })
        } else if (part.toLowerCase().includes('transformation')) {
          items.push({ date: '—', type: 'ceremony', label: `Ceremony: ${part}`, dims: { idn: 50, wis: 30 }, total: 80 })
        } else if (part.toLowerCase().includes('review')) {
          items.push({ date: '—', type: 'ceremony', label: `Ceremony: ${part}`, dims: { idn: 15, wis: 10 }, total: 25 })
        } else if (part.toLowerCase().includes('initiation')) {
          items.push({ date: '—', type: 'ceremony', label: `Ceremony: ${part}`, dims: { idn: 20, wis: 10 }, total: 30 })
        } else {
          items.push({ date: '—', type: 'ceremony', label: `Ceremony: ${part}`, dims: { idn: 20 }, total: 20 })
        }
      }
    }

    if (persona.com >= 100)
      items.push({ date: '—', type: 'task', label: 'Project task completions', dims: { com: Math.round(persona.com * 0.6) }, total: Math.round(persona.com * 0.6) })
    if (persona.soc >= 50)
      items.push({ date: '—', type: 'activity', label: 'Community conversations', dims: { soc: Math.round(persona.soc * 0.7) }, total: Math.round(persona.soc * 0.7) })
    if (persona.cre >= 30)
      items.push({ date: '—', type: 'activity', label: 'Creative output & posts', dims: { cre: Math.round(persona.cre * 0.8) }, total: Math.round(persona.cre * 0.8) })
    if (persona.srv >= 30)
      items.push({ date: '—', type: 'activity', label: 'Infrastructure & service work', dims: { srv: Math.round(persona.srv * 0.5) }, total: Math.round(persona.srv * 0.5) })

    items.sort((a, b) => b.total - a.total)
    return items
  }, [persona])

  const typeIcons: Record<string, string> = { ceremony: '✦', task: '✓', activity: '◆', quest: '⚡' }
  const typeColors: Record<string, string> = { ceremony: '#D65A93', task: '#5AC884', activity: '#6FB8D6', quest: '#E6C85A' }

  if (events.length === 0)
    return <div style={{ fontSize: 10, color: 'var(--text-muted)', fontStyle: 'italic', padding: '4px 0' }}>No recorded XP activity yet.</div>

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {events.map((ev, i) => (
        <div key={i} style={{
          display: 'flex', alignItems: 'flex-start', gap: 6,
          padding: '5px 8px', borderRadius: 6,
          background: 'var(--bg-elevated)',
        }}>
          <span style={{ fontSize: 10, color: typeColors[ev.type] || 'var(--text-muted)', flexShrink: 0, marginTop: 1 }}>
            {typeIcons[ev.type] || '•'}
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 10, color: 'var(--text-primary)', fontWeight: 500 }}>{ev.label}</div>
            <div style={{ fontSize: 9, color: 'var(--text-muted)', marginTop: 2, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {Object.entries(ev.dims).map(([k, v]) => {
                const dim = DIMENSIONS.find(d => d.key === k)
                return <span key={k} style={{ color: dim?.color || 'var(--text-muted)' }}>+{v} {k.toUpperCase()}</span>
              })}
            </div>
          </div>
          <span style={{ fontSize: 10, fontWeight: 700, color: '#5AC884', flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>+{ev.total}</span>
        </div>
      ))}
      <div style={{ fontSize: 9, color: 'var(--text-muted)', fontStyle: 'italic', marginTop: 2, padding: '0 4px' }}>
        Detailed event timestamps available when the live XP engine ships.
      </div>
    </div>
  )
}

export default function PersonaMetricsGrid() {
  const navigate = useNavigate()
  const [sortBy, setSortBy] = useState<SortKey>('total')
  const [sortDesc, setSortDesc] = useState(true)
  const [filterLevel, setFilterLevel] = useState<number | null>(null)
  const [filterCircle, setFilterCircle] = useState<string | null>(null)
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set())
  const [allCollapsed, setAllCollapsed] = useState(false)
  const [expandedFeed, setExpandedFeed] = useState<string | null>(null)

  const toggleCollapse = (name: string) => {
    setCollapsed(prev => {
      const next = new Set(prev)
      if (next.has(name)) next.delete(name)
      else next.add(name)
      return next
    })
  }

  const toggleAllCollapsed = () => {
    if (allCollapsed) {
      setCollapsed(new Set())
      setAllCollapsed(false)
    } else {
      setCollapsed(new Set(personas.map(p => p.name)))
      setAllCollapsed(true)
    }
  }

  const personas: PersonaMetrics[] = useMemo(() =>
    Object.entries(personaXpData.personas).map(([name, data]) => ({ name, ...data as any })),
    []
  )

  const circles = useMemo(() => [...new Set(personas.map(p => p.circle))].sort(), [personas])

  const filtered = useMemo(() => {
    let list = [...personas]
    if (filterLevel !== null) list = list.filter(p => p.level === filterLevel)
    if (filterCircle) list = list.filter(p => p.circle === filterCircle)
    list.sort((a, b) => {
      let va: number | string, vb: number | string
      if (sortBy === 'name') { va = a.name; vb = b.name }
      else { va = a[sortBy as keyof PersonaMetrics] as number; vb = b[sortBy as keyof PersonaMetrics] as number }
      if (typeof va === 'string') return sortDesc ? vb.toString().localeCompare(va.toString()) : va.toString().localeCompare(vb.toString())
      return sortDesc ? (vb as number) - (va as number) : (va as number) - (vb as number)
    })
    return list
  }, [personas, sortBy, sortDesc, filterLevel, filterCircle])

  const handleSort = (key: SortKey) => {
    if (sortBy === key) setSortDesc(!sortDesc)
    else { setSortBy(key); setSortDesc(true) }
  }

  const sortIndicator = (key: SortKey) =>
    sortBy === key ? (sortDesc ? ' ▾' : ' ▴') : ''

  return (
    <div>
      {/* Controls bar */}
      <div style={{
        display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center',
        padding: '10px 14px', borderRadius: 10,
        background: 'var(--bg-surface)', border: '1px solid var(--border)',
      }}>
        {/* Filters */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8 }}>Filter</span>
          <select
            value={filterLevel ?? ''}
            onChange={e => setFilterLevel(e.target.value === '' ? null : Number(e.target.value))}
            style={{
              padding: '4px 10px', fontSize: 11, borderRadius: 6,
              background: 'var(--bg-elevated)', color: 'var(--text-primary)',
              border: '1px solid var(--border)', fontFamily: 'inherit',
              cursor: 'pointer',
            }}
          >
            <option value="">All Levels</option>
            {[0, 1, 2, 3].map(l => (
              <option key={l} value={l}>{LEVEL_NAMES[l]}</option>
            ))}
          </select>
          <select
            value={filterCircle ?? ''}
            onChange={e => setFilterCircle(e.target.value || null)}
            style={{
              padding: '4px 10px', fontSize: 11, borderRadius: 6,
              background: 'var(--bg-elevated)', color: 'var(--text-primary)',
              border: '1px solid var(--border)', fontFamily: 'inherit',
              cursor: 'pointer',
            }}
          >
            <option value="">All Circles</option>
            {circles.map(c => (
              <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
            ))}
          </select>
        </div>

        <div style={{ width: 1, height: 20, background: 'var(--border)', flexShrink: 0 }} />

        {/* Sort pills */}
        <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.8, marginRight: 2 }}>Sort</span>
          {([
            { key: 'name' as SortKey, label: 'Name' },
            { key: 'total' as SortKey, label: 'XP' },
            { key: 'level' as SortKey, label: 'Level' },
            ...DIMENSIONS.map(d => ({ key: d.key as SortKey, label: d.key.toUpperCase() })),
          ]).map(({ key, label }) => {
            const isActive = sortBy === key
            const dimColor = DIMENSIONS.find(d => d.key === key)?.color
            return (
              <button
                key={key}
                onClick={() => handleSort(key)}
                style={{
                  padding: '3px 10px', borderRadius: 12, fontSize: 10,
                  background: isActive ? (dimColor ? `${dimColor}20` : 'var(--bg-elevated)') : 'transparent',
                  color: isActive ? (dimColor || 'var(--text-primary)') : 'var(--text-muted)',
                  border: `1px solid ${isActive ? (dimColor ? `${dimColor}40` : 'var(--border-strong, #3a3a44)') : 'transparent'}`,
                  cursor: 'pointer', fontFamily: 'inherit', fontWeight: isActive ? 700 : 400,
                  transition: 'all 0.15s',
                }}
              >
                {label}{sortIndicator(key)}
              </button>
            )
          })}
        </div>

        <div style={{ flex: 1 }} />
        <button
          onClick={toggleAllCollapsed}
          style={{
            padding: '3px 10px', borderRadius: 12, fontSize: 10,
            background: 'transparent', color: 'var(--text-muted)',
            border: '1px solid var(--border)', cursor: 'pointer', fontFamily: 'inherit',
          }}
        >
          {allCollapsed ? 'Expand All' : 'Minimize All'}
        </button>
        <span style={{ fontSize: 10, color: 'var(--text-muted)', fontVariantNumeric: 'tabular-nums' }}>
          {filtered.length}/{personas.length}
        </span>
      </div>

      {/* Grid */}
      <div style={{
        display: 'grid', gap: 14,
        gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
      }}>
        {filtered.map(p => {
          const progress = getProgress(p)
          const levelColor = LEVEL_COLORS[p.level]
          const nextThresh = getNextThreshold(p.level)
          const isCollapsed = collapsed.has(p.name)
          return (
            <div key={p.name} className="persona-metric-card" style={{
              background: 'var(--bg-surface)',
              border: '1px solid var(--border)',
              borderRadius: 12, padding: 16,
              borderLeft: `3px solid ${levelColor}`,
              transition: 'border-color 0.2s, box-shadow 0.2s',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = levelColor; e.currentTarget.style.boxShadow = `0 0 12px ${levelColor}15` }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.borderLeftColor = levelColor; e.currentTarget.style.boxShadow = 'none' }}
            >
              {/* Header */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: isCollapsed ? 0 : 12 }}>
                <PersonaAvatar name={p.name} size={36} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6, color: 'var(--text-primary)' }}>
                    <span onClick={(e) => { e.stopPropagation(); navigate(`/evolve/persona/${p.name}`) }} style={{ cursor: 'pointer', transition: 'color 0.15s' }} onMouseEnter={e => { e.currentTarget.style.color = AGORA_ACCENT }} onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-primary)' }}>
                      {p.name.charAt(0).toUpperCase() + p.name.slice(1)}
                    </span>
                    <span style={{
                      fontSize: 9, padding: '2px 8px', borderRadius: 10,
                      background: `${levelColor}18`, color: levelColor,
                      fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase',
                      border: `1px solid ${levelColor}30`,
                    }}>
                      {p.level_name}
                    </span>
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 1 }}>
                    {p.circle.charAt(0).toUpperCase() + p.circle.slice(1)} Circle
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 20, fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: 'var(--text-primary)', lineHeight: 1 }}>
                    {p.total.toLocaleString()}
                  </div>
                  <div style={{ fontSize: 9, color: 'var(--text-muted)', marginTop: 2, textTransform: 'uppercase', letterSpacing: 1 }}>XP</div>
                </div>
                <button
                  onClick={() => toggleCollapse(p.name)}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: 'var(--text-muted)', fontSize: 12, padding: '2px 4px',
                    transition: 'color 0.15s',
                  }}
                  title={isCollapsed ? 'Expand' : 'Minimize'}
                >
                  {isCollapsed ? '▸' : '▾'}
                </button>
              </div>

              {isCollapsed ? null : (<>

              {/* Radar Chart */}
              <div style={{ display: 'flex', justifyContent: 'center', margin: '4px 0 10px' }}>
                <RadarChart persona={p} size={140} />
              </div>

              {/* Dimension bars with threshold markers */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 12 }}>
                {DIMENSIONS.map(dim => {
                  const val = p[dim.key as DimKey]
                  const pct = Math.min(100, (val / 500) * 100)
                  const isBottleneck = p.bottleneck === dim.key || p.bottleneck === 'all'
                  const threshPct = nextThresh ? Math.min(100, (nextThresh[dim.key] / 500) * 100) : null
                  const metThresh = nextThresh ? val >= nextThresh[dim.key] : true
                  return (
                    <div key={dim.key} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <DimTooltip dim={dim}>
                        <span style={{
                          fontSize: 9, width: 30, display: 'inline-block', textAlign: 'right', fontWeight: 600,
                          color: isBottleneck ? '#ef4444' : 'var(--text-muted)',
                          textTransform: 'uppercase', letterSpacing: 0.3,
                        }}>
                          {dim.key}
                        </span>
                      </DimTooltip>
                      <div style={{ flex: 1, height: 5, borderRadius: 3, background: 'var(--bg-elevated)', overflow: 'visible', position: 'relative' }}>
                        <div style={{
                          width: `${pct}%`, height: '100%', borderRadius: 3,
                          background: isBottleneck
                            ? 'linear-gradient(90deg, #ef4444, #f87171)'
                            : `linear-gradient(90deg, ${dim.color}cc, ${dim.color})`,
                          transition: 'width 0.3s',
                        }} />
                        {threshPct !== null && (
                          <div style={{
                            position: 'absolute', left: `${threshPct}%`, top: -1, bottom: -1,
                            width: 1.5, background: metThresh ? '#5AC88466' : 'var(--text-muted)',
                            opacity: metThresh ? 0.6 : 0.3,
                          }} />
                        )}
                      </div>
                      <span style={{
                        fontSize: 9, width: 28, textAlign: 'right',
                        color: isBottleneck ? '#ef4444' : 'var(--text-muted)',
                        fontVariantNumeric: 'tabular-nums', fontWeight: isBottleneck ? 600 : 400,
                      }}>
                        {val}
                      </span>
                    </div>
                  )
                })}
              </div>

              {/* Level progress */}
              {p.level < 3 && (
                <div style={{
                  padding: '8px 10px', borderRadius: 8,
                  background: `${LEVEL_COLORS[p.level + 1]}08`,
                  border: `1px solid ${LEVEL_COLORS[p.level + 1]}20`,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                    <span style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 500 }}>
                      → {LEVEL_NAMES[p.level + 1]}
                    </span>
                    <span style={{
                      fontSize: 11, fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                      color: progress.pct >= 80 ? '#5AC884' : progress.pct >= 50 ? LEVEL_COLORS[p.level + 1] : 'var(--text-muted)',
                    }}>
                      {progress.pct}%
                    </span>
                  </div>
                  <div style={{ height: 4, borderRadius: 2, background: 'var(--bg-elevated)', overflow: 'hidden' }}>
                    <div style={{
                      width: `${progress.pct}%`, height: '100%', borderRadius: 2,
                      background: `linear-gradient(90deg, ${LEVEL_COLORS[p.level + 1]}88, ${LEVEL_COLORS[p.level + 1]})`,
                      transition: 'width 0.3s',
                    }} />
                  </div>
                  <div style={{ fontSize: 9, marginTop: 3, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span style={{
                      display: 'inline-block', width: 5, height: 5, borderRadius: '50%',
                      background: '#ef4444', flexShrink: 0,
                    }} />
                    <span style={{ color: '#ef4444', fontWeight: 500 }}>
                      Blocked by {progress.blocking}
                    </span>
                    <span style={{ color: 'var(--text-muted)' }}>
                      ({progress.current}/{progress.required})
                    </span>
                  </div>
                </div>
              )}
              {p.level >= 3 && (
                <div style={{
                  padding: '6px 10px', borderRadius: 8,
                  background: `${LEVEL_COLORS[3]}10`,
                  border: `1px solid ${LEVEL_COLORS[3]}25`,
                  textAlign: 'center',
                }}>
                  <span style={{ fontSize: 10, color: LEVEL_COLORS[3], fontWeight: 600 }}>Steward</span>
                </div>
              )}

              {/* Ceremony + XP Activity Feed */}
              <div
                onClick={() => setExpandedFeed(expandedFeed === p.name ? null : p.name)}
                style={{
                  marginTop: 8, paddingTop: 8,
                  borderTop: '1px solid var(--border)',
                  cursor: 'pointer',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ fontSize: 10, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 5 }}>
                    <span style={{ color: '#D65A93' }}>✦</span>
                    <span style={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 9 }}>Ceremony:</span>
                    <span style={{ fontStyle: 'italic', color: 'var(--text-muted)', fontSize: 9 }}>
                      {(!p.ceremony || p.ceremony === 'None') ? 'No ceremonies yet' : p.ceremony}
                    </span>
                  </div>
                  <span style={{
                    fontSize: 10, color: 'var(--text-muted)',
                    transition: 'transform 0.2s',
                    transform: expandedFeed === p.name ? 'rotate(180deg)' : 'none',
                  }}>▾</span>
                </div>
                <div style={{ fontSize: 9, color: 'var(--text-muted)', marginTop: 2 }}>
                  {expandedFeed === p.name ? 'Hide' : 'View'} XP activity feed
                </div>
              </div>

              {expandedFeed === p.name && (
                <div style={{ marginTop: 8 }}>
                  <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    XP Growth Feed
                  </div>
                  <XpActivityFeed persona={p} />
                </div>
              )}
              </>)}
            </div>
          )
        })}
      </div>
    </div>
  )
}
